// ══════════════════════════════════════════════════════════════
// IMPORTADOR DE XP: escritura en Firestore (agregado 27/09/2026)
//
// Separado de firebase-config.ts para no seguir agrandando ese archivo.
// Escribe con writeBatch (todo o nada por lote) y después deja un
// registro en la colección "ImportacionesXP" para poder auditar.
// ══════════════════════════════════════════════════════════════

import {
  arrayUnion,
  collection,
  doc,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase-config";
import type { XpPlanItem } from "./utils/xpImport";

// Firestore permite 500 operaciones por lote; dejamos margen.
const BATCH_SIZE = 400;

export interface ApplyXpImportParams {
  items: XpPlanItem[];
  fileName: string;
  adminEmail: string;
  notifyUsers: boolean;
}

export interface ApplyXpImportResult {
  updated: number;
  logSaved: boolean;
}

const buildNotification = (item: XpPlanItem) => ({
  Title: "¡Tu experiencia se actualizó!",
  Description:
    item.deltaExp > 0
      ? `Sumaste ${item.deltaExp} XP por tus actividades y parciales aprobados.`
      : "Actualizamos tu XP con las notas más recientes de la cursada.",
  Checked: false,
  Icon: "⚡",
  Date: new Date(),
});

export async function ApplyXpImport({
  items,
  fileName,
  adminEmail,
  notifyUsers,
}: ApplyXpImportParams): Promise<ApplyXpImportResult> {
  const toUpdate = items.filter(
    (item) => item.status === "update" && item.user && item.after,
  );

  for (let start = 0; start < toUpdate.length; start += BATCH_SIZE) {
    const batch = writeBatch(db);
    toUpdate.slice(start, start + BATCH_SIZE).forEach((item) => {
      const after = item.after!;
      const updates: Record<string, any> = {
        CurrentExp: after.currentExp,
        Nivel: after.nivel,
        XpAcademica: after.xpAcademica,
        Skill: after.skills,
        XpActualizadaEl: serverTimestamp(),
      };
      // Solo avisamos cuando la XP realmente cambió, no por un ajuste de skills.
      if (notifyUsers && item.deltaExp !== 0) {
        updates.Notifications = arrayUnion(buildNotification(item));
      }
      batch.update(doc(db, "Usuarios", item.user!.id), updates);
    });
    // Si un lote falla, se lanza el error y no se sigue con los demás.
    await batch.commit();
  }

  // El registro es secundario: si las reglas todavía no permiten la
  // colección, la importación ya quedó hecha y solo avisamos.
  let logSaved = true;
  try {
    await setDoc(doc(collection(db, "ImportacionesXP")), {
      Fecha: serverTimestamp(),
      Archivo: fileName,
      Admin: adminEmail,
      Actualizados: toUpdate.length,
      SinCambios: items.filter((i) => i.status === "unchanged").length,
      NoEncontrados: items
        .filter((i) => i.status === "not_found")
        .map((i) => i.row.email),
      ConErrores: items
        .filter((i) => i.status === "invalid")
        .map((i) => ({ Email: i.row.email, Errores: i.row.errors })),
      Detalle: toUpdate.map((i) => ({
        UserId: i.user!.id,
        Email: i.row.email,
        ExpAntes: i.before!.currentExp,
        ExpDespues: i.after!.currentExp,
        NivelAntes: i.before!.nivel,
        NivelDespues: i.after!.nivel,
      })),
    });
  } catch (error) {
    console.error("No se pudo guardar el registro de importación:", error);
    logSaved = false;
  }

  return { updated: toUpdate.length, logSaved };
}
